import { inject, Injectable } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';

import { NarrativeRichTextMarker } from '../constants';
import { BarChartDatePeriod } from '../models';

@Injectable({
  providedIn: 'root',
})
export class FormatService {
  private readonly datePipe = inject(DatePipe);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly decimalPipe = inject(DecimalPipe);

  date(date: Date): string {
    return date ? this.datePipe.transform(date, 'shortDate') : undefined;
  }

  // "24 Sep 2026" - a date with the month spelled, for screens that show dates in
  // prose rather than in a dense grid. Added 2026-09-24 so the Workbench apps define
  // that format ONCE instead of repeating `| date: 'd MMM yyyy'` in every template
  // (Chris, PR 2348).
  //
  // ⚠️ NOT the same as date() above, which is `shortDate` (24/09/2026). They are two
  // deliberate looks, so swapping one for the other changes what every screen shows.
  dateLong(date: string | Date): string {
    return date ? this.datePipe.transform(date, 'd MMM yyyy') : '';
  }

  dateTime(date: Date): string {
    return date
      ? `${this.datePipe.transform(date, 'shortDate')} ${this.datePipe.transform(date, 'shortTime')}`
      : undefined;
  }

  dayOfWeekShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEE') : undefined;
  }

  dayOfWeekLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'EEEE') : undefined;
  }

  dayOfMonthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'd') : undefined;
  }

  dayOfMonthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'dd') : undefined;
  }

  monthShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLL') : undefined;
  }

  monthLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'LLLL') : undefined;
  }

  yearShort(date: Date): string {
    return date ? this.datePipe.transform(date, 'yy') : undefined;
  }

  yearLong(date: Date): string {
    return date ? this.datePipe.transform(date, 'yyyy') : undefined;
  }

  dateForPeriods(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.date(date);
      case BarChartDatePeriod.Month:
        return this.monthLong(date) + ' ' + this.yearLong(date);
      case BarChartDatePeriod.Year:
        return this.yearLong(date);
      default:
        return undefined;
    }
  }

  dateItemsForPeriod(date: Date, period: BarChartDatePeriod): string {
    switch (period) {
      case BarChartDatePeriod.Week:
        return this.dayOfWeekShort(date);
      case BarChartDatePeriod.Month:
        return this.dayOfMonthShort(date);
      case BarChartDatePeriod.Year:
        return this.monthShort(date);
      default:
        return undefined;
    }
  }

  number(value: number | string, decimalPlaces = 2): string {
    const valueNumber = this.parseNumberString(value);
    const roundValue = this.roundNumber(valueNumber, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.decimalPipe.transform(roundValue, digitsInfo);
  }

  numberUOM(value: number | string, unitOfMeasure: string, decimalPlaces = 2): string {
    return `${this.number(value, decimalPlaces)} ${unitOfMeasure}`;
  }

  percent(value: number | string, decimalPlaces = 2): string {
    return `${this.number(value, decimalPlaces)}%`;
  }

  currency(value: number | string, currencySymbol?: string, decimalPlaces = 2): string {
    const numberValue = this.parseNumberString(value);
    const roundValue = this.roundNumber(numberValue, decimalPlaces);
    const digitsInfo = this.digitsInfo(decimalPlaces);
    return this.currencyPipe.transform(roundValue, undefined, `${currencySymbol ?? ''} `, digitsInfo);
  }

  currencyUOM(value: number | string, currencySymbol: string, unitOfMeasure: string, decimalPlaces = 2): string {
    return `${this.currency(value, currencySymbol, decimalPlaces)} ${unitOfMeasure}`;
  }

  numberShort(value: number): number | string {
    if (value >= 1_000_000) {
      return `${this.roundNumber(value / 1_000_000, 2)}M`;
    } else if (value >= 1_000) {
      return `${this.roundNumber(value / 1_000, 2)}K`;
    }
    return value;
  }

  currencyShort(value: number, currencySymbol: string): string {
    return `${currencySymbol}${this.numberShort(value)}`;
  }

  multiLineToHtml(multiLine: string): string {
    return multiLine ? this.valuesToHtml(multiLine?.replace(/\r/g, '').split('\n')) : '';
  }

  /**
   * A stored narrative-contract value (a narrative, or an email-body field such as Document
   * Message) as display HTML for a client-rendered report — the UI-side sibling of the API's
   * NarrativeHtml(). A MARKED value is HTML: it was sanitised on save, so it is emitted as it
   * stands, minus the marker, with the editor's non-breaking spaces put back (they stop text
   * wrapping — see the Quill traps in ui.md). Anything else is legacy plain text: escaped,
   * then its line breaks become &lt;br /&gt;, which is what multiLineToHtml always produced —
   * ⚠️ prefer this over a bare multiLineToHtml for any field that can hold the marker, or a
   * rich value renders as visible tags.
   *
   * `replacements` substitutes tag tokens the way the despatch-note templates use them —
   * pass both sides RAW ({ '<--CompanyName-->': name }). In display HTML a token appears
   * HTML-ESCAPED whichever way the value was stored (a plain value is escaped here, the
   * rich-text editor escapes what the user types), so the escaped spelling is matched and
   * the replacement value is escaped on the way in. Every occurrence is replaced.
   */
  narrativeToHtml(value: string, replacements?: Record<string, string>): string {
    let html: string;
    if (!value) {
      html = '';
    } else if (value.startsWith(NarrativeRichTextMarker)) {
      html = value.slice(NarrativeRichTextMarker.length).replace(/&nbsp;/g, ' ');
    } else {
      html = this.multiLineToHtml(this.escapeHtml(value));
    }

    for (const [token, replacement] of Object.entries(replacements ?? {})) {
      html = html.split(this.escapeHtml(token)).join(this.escapeHtml(replacement));
    }

    return html;
  }

  /**
   * Bytes as a readable file size — "84 KB", "1.2 MB" — matching the API's FormatSize
   * (DocumentAttachmentController), so a size shown client-side (the email modal's
   * attachment strip) agrees with one the API formatted (fileSizeDescription).
   */
  fileSize(bytes: number): string {
    if (!bytes) {
      return '';
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    const kb = bytes / 1024;
    return kb < 1024 ? `${Math.round(kb * 10) / 10} KB` : `${Math.round((kb / 1024) * 10) / 10} MB`;
  }

  /** For values interpolated into HTML a template will render raw. */
  escapeHtml(value: string): string {
    return (value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  valuesToHtml(values: string[]): string {
    return values.join('<br />');
  }

  /**
   * Picks a size-reducing class so a long description still fits the two lines that
   * `.stock-label-description` clamps it to, instead of being cut off. Used from label
   * templates as `<!--descriptionSizeClass(productDescription)-->`.
   *
   * The thresholds are character counts that fit two lines at each size on the medium
   * labels; below the first threshold no class is added and the base size applies.
   */
  descriptionSizeClass(description: string): string {
    const length = description?.length ?? 0;
    if (length > 90) {
      return 'stock-label-description-x-small';
    }
    if (length > 60) {
      return 'stock-label-description-small';
    }
    return '';
  }

  roundNumber(value: number, decimalPlaces: number) {
    const roundNumber = Math.pow(10, decimalPlaces);
    return Math.round(value * roundNumber) / roundNumber;
  }

  parseNumberString(value: number | string): number {
    if (typeof value === 'string') {
      let digitSeparatorIndex = value.lastIndexOf('.');
      if (digitSeparatorIndex === -1) {
        digitSeparatorIndex = value.lastIndexOf(',');
      }
      if (digitSeparatorIndex !== -1) {
        const whole = value.slice(0, digitSeparatorIndex).replace(/[,.]/g, '');
        const decimal = value.slice(digitSeparatorIndex + 1);
        return +`${whole}.${decimal}`;
      } else {
        return +value;
      }
    }

    return isNaN(value) ? 0 : value;
  }

  formatStringArrayToString(stringArray: string[]): string {
    return stringArray.join('\n');
  }

  private digitsInfo(decimalPlaces: number) {
    return `1.${decimalPlaces}-${decimalPlaces}`;
  }
}
